import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { downloadErrorAlert, getAuthorities, saveAsFile } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/private-notices/PrivateNoticesAction";
import * as uiHelpers from "../PrivateNoticesUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { usePrivateNoticesUIContext } from "../PrivateNoticesUIContext";

export function PrivateNoticesTable({ 
  deletePrivateNotices
}) {
  // Private Notices UI Context
  const privateNoticesUIContext = usePrivateNoticesUIContext();
  const privateNoticesUIProps = useMemo(() => {
    return {
      ids: privateNoticesUIContext.ids,
      setIds: privateNoticesUIContext.setIds,
      queryParams: privateNoticesUIContext.queryParams,
      setQueryParams: privateNoticesUIContext.setQueryParams,
      openPrivateNoticeDetailPage: privateNoticesUIContext.openPrivateNoticeDetailPage
    };
  }, [privateNoticesUIContext]);

  // Getting curret state of Private Notices list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.privateNotices }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isDeletionAuthorized } = getAuthorities(authorities);
  
  // Private Notices Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    // clear selections list
    privateNoticesUIProps.setIds([]);
    dispatch(actions.getPrivateNotices(privateNoticesUIProps.queryParams));
    return () => dispatch(actions.cleanPrivateNoticesState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privateNoticesUIProps.queryParams, dispatch]);

  /** Belli Bir Eki İndirir */
  const downloadAttachment = (id, filename) => {
    dispatch(actions.downloadAttachment(id)).then(file => {
      if (!file) {
        downloadErrorAlert();
        return;
      }
      saveAsFile(file, filename);
  })}

  // Table columns
  const columns = [
    {
      dataField: "sender",
      text: "Gönderen",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "definition",
      text: "Tanımı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },
    {
      dataField: "explanation",
      text: "Açıklaması",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },
    {
      dataField: "createDate",
      text: "Tarih",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "100px"
      }
    },
    {
      dataField: "attachmentName",
      text: "Ek",
      sort: true,
      sortCaret: sortCaret,
      formatter: (cell, row) => (
        <a href="javascript:" onClick={() => downloadAttachment(row.id, row.attachmentName)}> {cell}</a>
      ),
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "receipentTypeName",
      text: "Gönderim Grubu",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "175px"
      }
    },
    {
      dataField: "isSent",
      text: "Durum",
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
      style: {
        minWidth: "100px"
      }
    },
    {
      dataField: "action",
      text: "İşlemler",
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        isDeletionAuthorized,
        openPrivateNoticeDetailPage: privateNoticesUIProps.openPrivateNoticeDetailPage,
        deletePrivateNotices
      },
      classes: "text-right pr-0",
      headerClasses: "text-right pr-3",
      style: {
        minWidth: "100px",
      }
    }
  ];

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: privateNoticesUIProps.queryParams.pageSize,
    page: privateNoticesUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ? entities : [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                privateNoticesUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: privateNoticesUIProps.ids,
                setIds: privateNoticesUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}